import _ from 'lodash';
import { swapMapEntries } from '@/trendData/itemStore.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';

/**
 * In-memory store for the results a table fetch returns. Like `itemRuntime.store.ts` these are
 * runtime-only and must never be persisted, but they are table-specific rather than shared across
 * item types, so they live in their own store.
 */

export interface TableValues {
  /** Aggregated series rendered by the table visualization */
  data?: unknown;
  /** Name of the signal being aggregated (the y-axis) */
  signalName?: string;
  /** Key of the statistic the table aggregates by */
  stat?: string;
  /** x-axis category labels */
  categories?: string[];
}

export interface TableRuntimeState {
  /** Fetched results keyed by item id; tables with no entry have not received results */
  values: Record<string, TableValues>;
}

export interface TableRuntimeActions {
  /**
   * Records the results carried by a table-results payload. Merges into the table's entry so
   * fields absent from the update keep their previous values, matching the item-cursor writes the
   * table store used to do
   */
  setValues(
    id: string,
    payload: {
      tableData?: unknown;
      signalName?: string;
      stat?: { key: string; timeUnits: string };
      categories?: string[];
    },
  ): void;
  /** Discards an item's results, for when its data is no longer valid to render */
  clearValues(id: string): void;
  /**
   * Moves entries to their swapped-in ids, mirroring the flux stores renaming items in place on
   * TREND_SWAP_ITEMS: the swapped-in item keeps rendering the previous values until its fetch
   * returns, and nothing is left keyed by an id that no longer exists.
   */
  swapValues(swaps: Record<string, string>): void;
  reset(): void;
}

export const initialState: TableRuntimeState = {
  values: {},
};

const STORE_NAME = 'TableRuntime';

const tableRuntimeActionsFactory: ActionsFactory<TableRuntimeState, TableRuntimeActions> = (set, get, resetState) => ({
  setValues: (id, payload) => {
    const update: TableValues = { data: payload.tableData, signalName: payload.signalName };
    if (payload.stat) {
      update.stat = payload.stat.key;
    }
    if (payload.categories) {
      update.categories = payload.categories;
    }

    const current = get().values[id];
    const merged = { ...current, ...update };
    if (_.isEqual(current, merged)) {
      return;
    }
    set((draft) => {
      draft.values[id] = merged;
    });
  },

  clearValues: (id) => {
    if (!(id in get().values)) {
      return;
    }
    set((draft) => {
      delete draft.values[id];
    });
  },

  swapValues: (swaps) => {
    if (!Object.keys(swaps).some((swappedOutId) => swappedOutId in get().values)) {
      return;
    }
    set((draft) => {
      swapMapEntries(draft.values, swaps);
    });
  },

  reset: resetState,
});

const [TableRuntimeStoreProvider, useTableRuntimeStoreInstance, useTableRuntimeSelector] = createStore(
  STORE_NAME,
  initialState,
  tableRuntimeActionsFactory,
);

export { TableRuntimeStoreProvider };

export const useTableValues = () => useTableRuntimeSelector((state) => state.values);

export const useTableRuntimeActions = () => useTableRuntimeStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateTableRuntimeStore(initialStateOverrides?: Partial<TableRuntimeState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    tableRuntimeActionsFactory,
    { storeName: STORE_NAME },
  );
}
